import { generateClient } from "aws-amplify/api";
import {
  datasetFilesByDatasetIdAndPeriodStart,
  datasetProcessingRunsByDatasetIdAndStartedAt,
} from "../graphql/queries";

// What curation did with a dataset's uploads (stages 3 to 5 of docs/plans/DATASET_API_INGEST_PLAN.md), read from
// the two catalog models the curate Lambda writes: DatasetProcessingRun (one row per build, compaction or
// cleanup) and DatasetFile (one row per curated Parquet file). Both are read-only through AppSync for the
// dataset's owner, its organization and admins; the reference client is scripts/datasetUploadTest/curated.js.

export type ProcessingRunType = "DAY_BUILD" | "MONTH_COMPACT" | "CLEANUP" | "REBUILD";
export type ProcessingRunStatus = "RUNNING" | "SUCCEEDED" | "FAILED";

export interface DatasetProcessingRunRow {
  id: string;
  datasetId: string;
  type: ProcessingRunType;
  status: ProcessingRunStatus;
  /** `2026-09-01` for a day build, a `first..last` range for a rebuild or cleanup, `orphans` for the orphan sweep */
  period: string | null;
  triggerUploadId: string | null;
  inputUploadIds: string[] | null;
  inputFileIds: string[] | null;
  outputFileIds: string[] | null;
  attempts: number | null;
  warnings: string[] | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface DatasetFileColumn {
  name: string;
  type: string;
}

export interface DatasetFileRow {
  id: string;
  datasetId: string;
  granularity: "DAY" | "MONTH";
  periodStart: string;
  periodEnd: string;
  minTime: string | null;
  maxTime: string | null;
  version: number;
  active: boolean;
  rowCount: number | null;
  sizeBytes: number | null;
  /** Parsed from the AWSJSON the API returns; a name and type per column of the file */
  columns: DatasetFileColumn[];
  createdAt: string;
  retiredAt: string | null;
  supersededBy: string | null;
  createdByRunId: string | null;
  s3Key: string;
}

export interface PagedResult<T> {
  items: T[];
  nextToken: string | null;
}

export const RUN_TYPE_LABELS: Record<ProcessingRunType, string> = {
  DAY_BUILD: "Day build",
  MONTH_COMPACT: "Month compaction",
  CLEANUP: "Cleanup",
  REBUILD: "Rebuild",
};

export const RUN_STATUS_LABELS: Record<ProcessingRunStatus, string> = {
  RUNNING: "Running",
  SUCCEEDED: "Succeeded",
  FAILED: "Failed",
};

const DEFAULT_PAGE_SIZE = 25;
// A filter runs after DynamoDB reads a page, so a page can come back short (or empty) while more remain: a
// filtered read takes bigger bites, and keeps reading until the page is full, but not without end for a filter
// that matches almost nothing
const FILTERED_READ_SIZE = 200;
const MAX_PAGES_PER_LOAD = 10;

const client = generateClient();

const asStringList = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;
  const list = value.filter((entry): entry is string => typeof entry === "string");
  return list.length ? list : null;
};

/** `columns` arrives as an AWSJSON string (or, from older rows, already an array) of `{ name, type }`. */
export const parseFileColumns = (value: unknown): DatasetFileColumn[] => {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((entry: any) => ({ name: String(entry?.name ?? ""), type: String(entry?.type ?? "") }))
    .filter((column) => column.name);
};

const toRunRow = (item: any): DatasetProcessingRunRow => ({
  id: item.id,
  datasetId: item.datasetId,
  type: item.type,
  status: item.status,
  period: item.period ?? null,
  triggerUploadId: item.triggerUploadId ?? null,
  inputUploadIds: asStringList(item.inputUploadIds),
  inputFileIds: asStringList(item.inputFileIds),
  outputFileIds: asStringList(item.outputFileIds),
  attempts: item.attempts ?? null,
  warnings: asStringList(item.warnings),
  error: item.error ?? null,
  startedAt: item.startedAt,
  finishedAt: item.finishedAt ?? null,
});

const toFileRow = (item: any): DatasetFileRow => ({
  id: item.id,
  datasetId: item.datasetId,
  granularity: item.granularity,
  periodStart: item.periodStart,
  periodEnd: item.periodEnd,
  minTime: item.minTime ?? null,
  maxTime: item.maxTime ?? null,
  version: item.version,
  active: Boolean(item.active),
  rowCount: item.rowCount ?? null,
  sizeBytes: item.sizeBytes ?? null,
  columns: parseFileColumns(item.columns),
  createdAt: item.createdAt,
  retiredAt: item.retiredAt ?? null,
  supersededBy: item.supersededBy ?? null,
  createdByRunId: item.createdByRunId ?? null,
  s3Key: item.s3Key,
});

const graphqlErrorMessage = (error: any, fallback: string): string => {
  const message = error?.errors?.[0]?.message || error?.message;
  return message ? `${fallback} ${message}` : fallback;
};

/** The dataset's processing runs, newest first. `status` filters on the server (FAILED for the failures view). */
export const fetchProcessingRuns = async (
  datasetId: string,
  options: { limit?: number; nextToken?: string | null; status?: ProcessingRunStatus | null } = {}
): Promise<PagedResult<DatasetProcessingRunRow>> => {
  const limit = options.limit || DEFAULT_PAGE_SIZE;
  const items: DatasetProcessingRunRow[] = [];
  let nextToken = options.nextToken || null;
  try {
    for (let page = 0; page < MAX_PAGES_PER_LOAD && items.length < limit; page++) {
      const response: any = await client.graphql({
        query: datasetProcessingRunsByDatasetIdAndStartedAt,
        variables: {
          datasetId,
          sortDirection: "DESC",
          limit: options.status ? FILTERED_READ_SIZE : limit,
          nextToken,
          ...(options.status ? { filter: { status: { eq: options.status } } } : {}),
        },
      });
      const result = response?.data?.datasetProcessingRunsByDatasetIdAndStartedAt;
      items.push(...(result?.items || []).filter(Boolean).map(toRunRow));
      nextToken = result?.nextToken || null;
      if (!nextToken) break;
    }
  } catch (error) {
    throw new Error(graphqlErrorMessage(error, "Could not load the processing runs."));
  }
  return { items, nextToken };
};

/** Newest period first, and within a period the highest version first (the index orders by period only). */
export const sortDatasetFiles = (files: DatasetFileRow[]): DatasetFileRow[] =>
  [...files].sort((a, b) => b.periodStart.localeCompare(a.periodStart) || b.version - a.version);

/** The dataset's curated files, newest period first. `activeOnly` leaves out the superseded versions. */
export const fetchDatasetFiles = async (
  datasetId: string,
  options: { limit?: number; nextToken?: string | null; activeOnly?: boolean } = {}
): Promise<PagedResult<DatasetFileRow>> => {
  const limit = options.limit || DEFAULT_PAGE_SIZE * 2;
  const items: DatasetFileRow[] = [];
  let nextToken = options.nextToken || null;
  try {
    for (let page = 0; page < MAX_PAGES_PER_LOAD && items.length < limit; page++) {
      const response: any = await client.graphql({
        query: datasetFilesByDatasetIdAndPeriodStart,
        variables: {
          datasetId,
          sortDirection: "DESC",
          limit: options.activeOnly ? Math.max(limit, FILTERED_READ_SIZE) : limit,
          nextToken,
          ...(options.activeOnly ? { filter: { active: { eq: true } } } : {}),
        },
      });
      const result = response?.data?.datasetFilesByDatasetIdAndPeriodStart;
      items.push(...(result?.items || []).filter(Boolean).map(toFileRow));
      nextToken = result?.nextToken || null;
      if (!nextToken) break;
    }
  } catch (error) {
    throw new Error(graphqlErrorMessage(error, "Could not load the curated files."));
  }
  return { items, nextToken };
};

/** "0.7 s", "12 s", "2 m 5 s"; "Running" until the run has a finish time. */
export const formatRunDuration = (startedAt: string | null | undefined, finishedAt: string | null | undefined): string => {
  if (!startedAt) return "";
  if (!finishedAt) return "Running";
  const ms = Date.parse(finishedAt) - Date.parse(startedAt);
  if (Number.isNaN(ms) || ms < 0) return "";
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)} s`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} m ${seconds % 60} s`;
};

/** A run's period in words: a day, a range, or the orphan sweep. */
export const describeRunPeriod = (run: Pick<DatasetProcessingRunRow, "type" | "period">): string => {
  const period = run.period || "";
  if (period === "orphans") return "Orphan sweep";
  if (period.includes("..")) return period.replace("..", " to ");
  return period;
};

/** `2026-09-01` for a day file, `2026-09` for a month file. */
export const describeFilePeriod = (file: Pick<DatasetFileRow, "granularity" | "periodStart">): string =>
  file.periodStart.slice(0, file.granularity === "MONTH" ? 7 : 10);

/** `v005`, the way the file's own name writes it. */
export const formatFileVersion = (version: number): string => `v${String(version).padStart(3, "0")}`;

/** `v005` out of a catalog id like `{datasetId}#DAY#2026-09-01#v005`; null when the id has no version. */
export const fileVersionFromId = (fileId: string | null | undefined): string | null => /#(v\d+)$/.exec(fileId || "")?.[1] ?? null;

export interface UploadCurationEntry {
  period: string;
  status: ProcessingRunStatus;
  /** The version the newest build of that day produced, when the run recorded its output */
  version: string | null;
  runId: string;
}

/**
 * Where an upload went in curation: the newest build of each day that merged it. A day's runs list every upload
 * merged for that day, so an upload shows up again in each later rebuild of the same day; only the latest per
 * day is kept. `runs` must be newest first, as fetchProcessingRuns returns them.
 */
export const describeUploadCuration = (runs: DatasetProcessingRunRow[], uploadId: string): UploadCurationEntry[] => {
  const seen = new Set<string>();
  const entries: UploadCurationEntry[] = [];
  for (const run of runs) {
    if (run.type !== "DAY_BUILD" && run.type !== "REBUILD") continue;
    const touched = run.triggerUploadId === uploadId || (run.inputUploadIds || []).includes(uploadId);
    if (!touched || !run.period || seen.has(run.period)) continue;
    seen.add(run.period);
    entries.push({
      period: run.period,
      status: run.status,
      version: fileVersionFromId(run.outputFileIds?.[0]),
      runId: run.id,
    });
  }
  return entries;
};

/** A short id for a table cell: the first block of a UUID. */
export const shortId = (id: string | null | undefined): string => (id ? id.slice(0, 8) : "");

const plural = (count: number, one: string, many: string = `${one}s`): string => `${count.toLocaleString()} ${count === 1 ? one : many}`;

/** What a run did, in a line: "Merged 9 uploads into v005", "Deleted 6 retired files". */
export const describeRunResult = (run: DatasetProcessingRunRow): string => {
  if (run.status === "RUNNING") return "Working…";
  const output = fileVersionFromId(run.outputFileIds?.[0]);
  switch (run.type) {
    case "DAY_BUILD":
    case "REBUILD": {
      if (run.status === "FAILED") return "The day was not published; the previous version stays active.";
      const uploads = run.inputUploadIds?.length ?? 0;
      return `Merged ${plural(uploads, "upload")}${output ? ` into ${output}` : ""}`;
    }
    case "MONTH_COMPACT": {
      if (run.status === "FAILED") return "The month was not compacted; the day files stay active.";
      const files = run.inputFileIds?.length ?? 0;
      return `Merged ${plural(files, "day file")}${output ? ` into ${output}` : ""}`;
    }
    case "CLEANUP": {
      if (run.period === "orphans") return "Checked for stored files the catalog does not know";
      const files = run.inputFileIds?.length ?? 0;
      return `Deleted ${plural(files, "retired file")}`;
    }
    default:
      return "";
  }
};
